import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listMyInvites, respondToInvite } from '../../api/owner'

/**
 * De uitnodigingen die op jouw adres binnenkwamen.
 *
 * Boven je eigen events, want dit is het enige op dit scherm dat om een antwoord
 * vraagt. Zijn er geen, dan staat er niets — een lege kop "uitnodigingen" is
 * ruis op een scherm dat je elke keer opnieuw opent.
 *
 * De mail wijst hierheen en draagt geen token: de uitnodiging hangt aan een
 * adres, en je bent al ingelogd met dat adres — anders had je hem niet gezien.
 * Een token in de mail zou daar niets bovenop leggen dat de registratie al niet
 * bepaalt.
 */
export function MyInvites() {
  const queryClient = useQueryClient()

  const invites = useQuery({
    queryKey: ['owner', 'invites'],
    queryFn: listMyInvites,
  })

  const respond = useMutation({
    mutationFn: ({ id, accept }: { id: string; accept: boolean }) => respondToInvite(id, accept),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['owner', 'invites'] })

      // Aannemen zet er een event bij in de lijst eronder.
      await queryClient.invalidateQueries({ queryKey: ['owner', 'events'] })
    },
  })

  const list = invites.data?.invites ?? []

  if (list.length === 0) {
    return null
  }

  return (
    <div className="px-5.5 pt-1 pb-2 lg:px-12 lg:pt-6">
      <p className="text-ink-faint mb-3 text-[11px] tracking-[0.18em] uppercase lg:mb-3.5 lg:text-xs">
        {list.length === 1 ? 'Uitnodiging' : 'Uitnodigingen'}
      </p>

      <div className="flex flex-col gap-2.5 lg:flex-row lg:flex-wrap lg:items-start lg:gap-4">
        {list.map((invite) => (
          <div
            key={invite.id}
            className="rounded-card border-accent/40 bg-notice border-[1.5px] p-4 lg:w-[420px] lg:p-5"
          >
            <p className="text-[15px] leading-[1.5] lg:text-base">
              <span className="font-semibold">{invite.invitedBy}</span> vraagt je om{' '}
              <span className="font-semibold">{invite.eventTitle}</span> mee te beheren.
            </p>

            <div className="mt-3.5 flex gap-2.5">
              <button
                type="button"
                disabled={respond.isPending}
                onClick={() => respond.mutate({ id: invite.id, accept: true })}
                className="bg-accent text-accent-ink h-11 flex-1 cursor-pointer rounded-full text-[15px] font-semibold transition hover:brightness-95 disabled:opacity-55"
              >
                Aannemen
              </button>
              <button
                type="button"
                disabled={respond.isPending}
                onClick={() => respond.mutate({ id: invite.id, accept: false })}
                className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink h-11 cursor-pointer rounded-full border px-5 text-[15px] transition-colors disabled:opacity-55"
              >
                Weigeren
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
